/**
 * useUmami — typed wrapper around the global Umami tracker
 *
 * Why a wrapper:
 *   - the umami tracker script loads `defer`, so `window.umami` may not exist
 *     yet when a Pinia action / page hook fires its first event
 *   - calling `window.umami.track(…)` directly during that window is a silent
 *     no-op and the event is lost
 *   - this wrapper queues calls until the tracker is ready, then flushes
 *
 * Behaviour:
 *   - `track(name, data?)`  → safe to call any time, queues if needed.
 *                             null/undefined properties are dropped, never sent
 *                             (see omitNullish below — Umami stores a JSON null
 *                             as the literal string "null", not as absence).
 *   - `identify(id, data?)` → no queue (rarely time-critical, drops if not ready)
 *   - `isReady()`           → boolean for callers that want to gate
 *
 * SSR-safe: calls during SSR are silent no-ops (no `window`).
 *
 * Site disabled? On non-production hostnames (DDEV/staging) the tracker plugin
 * (`plugins/umami-tracker.client.ts`) does not inject the script — so
 * `window.umami` stays undefined forever. After the retry budget expires the
 * scheduler stops polling. The queue is left intact (it's bounded by
 * `MAX_QUEUE_LENGTH` to prevent unbounded growth) and a later `track()` call
 * re-arms the scheduler.
 */

interface UmamiTrackerApi {
    track: (name: string, data?: Record<string, unknown>) => void
    identify: (id: string, data?: Record<string, unknown>) => void
}

declare global {
    interface Window {
        umami?: UmamiTrackerApi
    }
}

type QueuedEvent =
    | { kind: 'track'; name: string; data?: Record<string, unknown> }
    | { kind: 'identify'; id: string; data?: Record<string, unknown> }

/**
 * Drop null/undefined properties before they reach the tracker.
 *
 * Umami does not treat a JSON `null` as "absent" — it stores a row with
 * `data_type = 5` and the literal string `"null"` in `string_value`. Measured
 * 2026-08-12: 163,038 `sale_price` and 50,760 `regular_price` rows across the
 * database were exactly that. No report is *wrong* because of it, but an
 * `IS NOT NULL` filter does not do what it looks like, and it is a
 * quarter-million rows of noise in a 2.65 GB table.
 *
 * Absence is already the intended signal — `sale_price` is null precisely when
 * a product is not on sale — so omitting the key expresses that better than
 * sending a null does. Applied centrally here rather than per call site, so it
 * also holds for events added later.
 */
function omitNullish(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!data) return data
    const cleaned: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
        if (value !== null && value !== undefined) cleaned[key] = value
    }
    return cleaned
}

// Module-scoped queue + scheduler, shared across all useUmami() consumers.
// Note: this is a per-bundle global. In a typical single-Nuxt-app deployment
// that's fine; in micro-frontend / iframe scenarios where the bundle is
// loaded twice, the queue would be shared across instances.
const eventQueue: QueuedEvent[] = []
let flushScheduled = false

// Tunables — exported for tests if needed in future.
const POLL_INTERVAL_MS = 200
const POLL_MAX_TRIES = 25 // 25 × 200 ms = 5 s upper bound
const MAX_QUEUE_LENGTH = 100 // backpressure cap against misbehaving callers

function flushQueue() {
    if (typeof window === 'undefined' || !window.umami) return
    while (eventQueue.length > 0) {
        const ev = eventQueue.shift()!
        try {
            if (ev.kind === 'track') {
                window.umami.track(ev.name, ev.data)
            } else {
                window.umami.identify(ev.id, ev.data)
            }
        } catch {
            // swallow — analytics must never break the page; the event is
            // already removed from the queue via shift() above
        }
    }
    flushScheduled = false
}

function scheduleFlush() {
    if (flushScheduled) return
    flushScheduled = true

    const tryFlush = (triesLeft: number) => {
        if (typeof window !== 'undefined' && window.umami) {
            flushQueue()
            return
        }
        if (triesLeft <= 0) {
            // Give up polling. Leave the queue intact (it's bounded by
            // MAX_QUEUE_LENGTH); the next track() call will re-arm the
            // scheduler. Avoids the race where an event pushed exactly between
            // the budget check and a queue-wipe would be silently dropped.
            flushScheduled = false
            return
        }
        setTimeout(() => tryFlush(triesLeft - 1), POLL_INTERVAL_MS)
    }

    tryFlush(POLL_MAX_TRIES)
}

export function useUmami() {
    return {
        /**
         * Track a named custom event. Safe to call before the tracker has
         * loaded — events queue and flush when ready.
         * No-op on SSR (no window).
         */
        track(name: string, data?: Record<string, unknown>) {
            if (typeof window === 'undefined') return
            // Strip before both paths, so a queued event carries the same
            // payload a directly-sent one would.
            const payload = omitNullish(data)
            if (window.umami) {
                try {
                    window.umami.track(name, payload)
                } catch {
                    // swallow — never let analytics break the page
                }
                return
            }
            // Backpressure: drop new events once the queue is full so a
            // misbehaving caller (e.g. a Pinia action stuck in a loop on a
            // staging build where umami never loads) cannot grow it without
            // bound. 100 events is far more than any real user session ever
            // produces.
            if (eventQueue.length >= MAX_QUEUE_LENGTH) return
            eventQueue.push({ kind: 'track', name, data: payload })
            scheduleFlush()
        },

        /**
         * Identify a logged-in user (for cross-device stitching).
         * Queued like `track()` so a hydration-time identify() call doesn't
         * get lost if the deferred tracker hasn't loaded yet.
         */
        identify(id: string, data?: Record<string, unknown>) {
            if (typeof window === 'undefined') return
            if (window.umami) {
                try {
                    window.umami.identify(id, data)
                } catch {
                    // swallow
                }
                return
            }
            if (eventQueue.length >= MAX_QUEUE_LENGTH) return
            eventQueue.push({ kind: 'identify', id, data })
            scheduleFlush()
        },

        /**
         * Whether the global tracker is loaded and ready right now.
         */
        isReady(): boolean {
            return typeof window !== 'undefined' && Boolean(window.umami)
        },
    }
}
